import { useSyncExternalStore } from 'react'
import type { Lang } from './i18n'

/**
 * En qué hora se muestran las horas (hallazgo H-01, decidido el 23 de septiembre).
 *
 * Por omisión, en la de la **agencia**, con la zona siempre escrita («2:32 PM CDT»): los hechos, los
 * informes y los turnos son de la agencia, y «el incidente de las 4 PM» tiene que significar lo
 * mismo para el oficial, el jefe de turno y el acta. Quien trabaja desde otra zona puede pedir
 * «ver en mi hora», y entonces cambian **todas** las horas a la vez. Lo que no se hace es mezclar:
 * antes las horas salían en la zona del navegador y los turnos en la de la agencia, en la misma
 * pantalla y sin decir cuál era cuál.
 *
 * Todas las pantallas formatean por aquí. Un `toLocaleString` suelto vuelve a usar la zona del
 * navegador sin avisar.
 */
export type TimeMode = 'agency' | 'mine'

let agencyZone: string | null = null
let mode: TimeMode = leerModo()
let version = 0
const oyentes = new Set<() => void>()

function leerModo(): TimeMode {
  try {
    return localStorage.getItem('timeMode') === 'mine' ? 'mine' : 'agency'
  } catch {
    return 'agency'   // ventana privada o almacenamiento bloqueado: la de la agencia
  }
}

function avisar() {
  version++
  oyentes.forEach((o) => o())
}

const esZona = (z: string | null | undefined): z is string => {
  if (!z) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: z })
    return true
  } catch {
    return false
  }
}

/** La zona de la agencia (IANA), cuando se sabe. Mientras no, se usa la del navegador. */
export function setAgencyZone(zone: string | null | undefined): void {
  const nueva = esZona(zone) ? zone : null
  if (nueva === agencyZone) return
  agencyZone = nueva
  avisar()
}

export function setTimeMode(m: TimeMode): void {
  if (m === mode) return
  mode = m
  try {
    localStorage.setItem('timeMode', m)
  } catch {
    // recordar es una comodidad
  }
  avisar()
}

export const browserZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone

/** La zona en la que se muestra ahora. */
export function displayZone(): string {
  return mode === 'mine' || !agencyZone ? browserZone() : agencyZone
}

/** Minutos de diferencia con UTC de una zona en un instante (−300 para CDT). */
export function offsetMinutes(zone: string, at: Date = new Date()): number {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(at).map((x) => [x.type, x.value]))
  const comoUtc = Date.UTC(+p.year!, +p.month! - 1, +p.day!, +p.hour!, +p.minute!, +p.second!)
  return Math.round((comoUtc - at.getTime()) / 60_000)
}

/** Si quien mira está en otra hora que la agencia: solo entonces tiene sentido ofrecer «ver en mi hora». */
export function zonesDiffer(at: Date = new Date()): boolean {
  return agencyZone !== null && offsetMinutes(agencyZone, at) !== offsetMinutes(browserZone(), at)
}

/** La abreviatura de la zona en ese instante: CDT, CST, GMT+2… Siempre la de EE. UU. si la tiene. */
export function zoneLabel(at: Date = new Date(), zone: string = displayZone()): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
    .formatToParts(at).find((x) => x.type === 'timeZoneName')?.value ?? zone
}

const locale = (lang: Lang) => (lang === 'es' ? 'es' : 'en')

/** Hora y minutos, con la zona: «14:32 CDT». */
export function fmtTime(iso: string, lang: Lang): string {
  const d = new Date(iso)
  const hora = new Intl.DateTimeFormat(locale(lang), { hour: '2-digit', minute: '2-digit', timeZone: displayZone() }).format(d)
  return `${hora} ${zoneLabel(d)}`
}

/** Fecha y hora, con la zona. */
export function fmtDateTime(iso: string, lang: Lang): string {
  const d = new Date(iso)
  const texto = new Intl.DateTimeFormat(locale(lang), { dateStyle: 'medium', timeStyle: 'short', timeZone: displayZone() }).format(d)
  return `${texto} ${zoneLabel(d)}`
}

/** Solo la fecha, en la zona que se muestra (sin zona: un día no la necesita). */
export function fmtDate(iso: string, lang: Lang, opts: Intl.DateTimeFormatOptions = {}): string {
  return new Intl.DateTimeFormat(locale(lang), { timeZone: displayZone(), ...opts }).format(new Date(iso))
}

/** `AAAA-MM-DD` de un instante en una zona. */
export function dateIn(zone: string, at: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(at)
}

/**
 * Hoy, en la agencia. La fecha de un turno es de la agencia: el EVENING del 23 es el 23 en Fort
 * Worth, lo vea quien lo vea. Antes salía de `toISOString()`, que es UTC: por la tarde en Texas ya
 * era «mañana».
 */
export function agencyToday(at: Date = new Date()): string {
  return dateIn(agencyZone ?? browserZone(), at)
}

/** Si un instante cae en el mismo día que hoy, en la zona que se muestra. */
export function isToday(iso: string, at: Date = new Date()): boolean {
  return dateIn(displayZone(), new Date(iso)) === dateIn(displayZone(), at)
}

/**
 * Una hora escrita sin zona (`AAAA-MM-DDTHH:mm`, lo que da un campo de fecha y hora) a UTC,
 * leída en la zona que se muestra: la misma en que quien la escribió ve todas las demás.
 */
export function wallToIso(local: string, zone: string = displayZone()): string {
  const [fecha = '', hora = '00:00'] = local.split('T')
  const [y, mo, d] = fecha.split('-').map(Number)
  const [h, mi] = hora.split(':').map(Number)
  const comoUtc = Date.UTC(y!, mo! - 1, d, h, mi)
  // Dos vueltas: el desfase de la primera suposición puede cambiar si cruza un cambio de horario.
  let t = comoUtc - offsetMinutes(zone, new Date(comoUtc)) * 60_000
  t = comoUtc - offsetMinutes(zone, new Date(t)) * 60_000
  return new Date(t).toISOString()
}

const suscribir = (o: () => void) => { oyentes.add(o); return () => { oyentes.delete(o) } }

/**
 * Para que una pantalla se vuelva a dibujar al cambiar la zona o el modo. Devuelve un número que
 * cambia con cada cambio; lo que importa es suscribirse.
 */
export function useTimeVersion(): number {
  return useSyncExternalStore(suscribir, () => version)
}

export const timeMode = (): TimeMode => mode
export const knownAgencyZone = (): string | null => agencyZone
